import {
  DynamicFormData,
  GDMConditionalRender,
  GDMContainer,
  GDMDynamicFormFields,
  GDMLoading,
  GDMPageCard,
} from 'components';
import useLanguageSwitcher from 'core/hooks/useLanguageSwitcher';
import UserProfileStore from 'core/store/stores/UserProfileStore/UserProfileStore';
import { useStore } from 'effector-react';
import useModal from 'hooks/useModal/useModal';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { dynamicFormValidateForm } from 'utils';
import { errorHandler } from 'utils/form/errorHandler';
import { UsersAdministratorService } from 'core/api/services/administrator';
import { AdminEditPageComponent } from '../AdminEditPage.type';
import { updateAdmin } from '../utils/update-admin';

const DEFAULT_ADMIN: Partial<DynamicFormData> = {
  name: '',
  contactEmail: '',
  phoneNumber: '',
  documentNumber: '',
};

const AdminEditPage: AdminEditPageComponent = () => {
  const { getLabelTranslation, currentLanguage } = useLanguageSwitcher();
  const {
    user: { countryName },
  } = useStore(UserProfileStore);

  const [admin, setAdmin] = useState<Partial<DynamicFormData>>(DEFAULT_ADMIN);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const [isSubmiting, setIsSubmiting] = useState(false);
  const [entityType, setEntityType] = useState('Administrador');
  const { openModal, closeModal } = useModal();
  const { adminId } = useParams();

  useEffect(() => {
    setIsLoading(true);
    setIsSubmiting(true);
    setEntityType('Administrador');

    UsersAdministratorService.getAdmById(adminId)
      .then(response => {
        const data = response;

        setAdmin({
          ...data,
          id: adminId,
          contactEmail: data.email,
        });
      })
      .catch(error => {
        console.error(error);
      })
      .finally(() => {
        setIsSubmiting(false);
        setIsLoading(false);
      });
  }, [currentLanguage]);

  useEffect(() => {
    if (admin.documentNumber) {
      setDocumentTypeId();
    }

    if (admin.countryId) {
      setCountryId();
    }
  }, [admin.documentNumber, admin.countryId]);

  useEffect(() => {
    if (countryName) {
      setCountryId();
    }
  }, [countryName]);

  const handleValidate = adminFormData =>
    dynamicFormValidateForm({
      formData: adminFormData,
      countryName,
      entityType,
      getLabelTranslation,
    });

  const onValidate = errors => {
    if (Object.keys(errors)?.length) {
      setIsSubmiting(false);
    }
    setErrors(errors);
  };

  const handleChange = (e: any) => {
    const {
      target: { value, name },
    } = e;

    setAdmin(adminData => ({ ...adminData, [name]: value }));
  };

  const handleCancel = () => {
    openModal({
      type: 'warning',
      title: getLabelTranslation('modal_title_lost_info'),
      message: getLabelTranslation('modal_message_lost_info'),
      onClose: closeModal,
      primaryAction: {
        label: getLabelTranslation('OK'),
        action: () => {
          navigate(-1);
          closeModal();
        },
      },
      secondAction: {
        label: getLabelTranslation('ReusableFormKey_Back'),
        action: () => {
          closeModal();
        },
      },
    });
  };

  const onError = errors => {
    errorHandler(errors, setErrors);

    openModal({
      type: 'error',
      title: getLabelTranslation('modal_tittle_update_error'),
      onClose: closeModal,
    });
  };

  const onInit = () => {
    setIsSubmiting(true);
  };

  const onSuccess = () => {
    openModal({
      type: 'success',
      title: getLabelTranslation('modal_tittle_update_success'),
      onClose: () => navigate('/admin-list'),
      primaryAction: {
        label: 'Ok',
        action: () => {
          closeModal();
          navigate('/admin-list');
        },
      },
    });
  };

  const onFinally = () => {
    setIsSubmiting(false);
  };

  const handleSubmit = async () => {
    await updateAdmin({
      admin,
      onError,
      onInit,
      onFinally,
      onSuccess,
      handleValidate,
      onValidate,
    });
  };

  const setDocumentTypeId = () => {
    if (countryName === 'Argentina') {
      setAdmin(prev => ({
        ...prev,
        documentTypeId: 'b85d6c71-e469-4268-a813-1bac98f596ae',
      }));
    } else {
      setAdmin(prev => ({
        ...prev,
        documentTypeId: 'd2334494-b6d5-4a92-93cb-a3cd38d54d0d',
      }));
    }
  };

  const setCountryId = () => {
    if (countryName === 'Argentina') {
      setAdmin(prev => ({
        ...prev,
        countryId: '628ff4ec-0435-460a-b4ca-bcea09bf9f5b',
      }));
    }

    if (countryName === 'Brasil') {
      setAdmin(prev => ({
        ...prev,
        countryId: '41981f5c-0e28-44f5-ab84-d6259659abee',
      }));
    }
  };

  return (
    <GDMPageCard
      breadcrumbs={`${getLabelTranslation('administrative')} > ${getLabelTranslation(
        'ver_cadastros',
      )} > ${getLabelTranslation('edit')}`}
      title={`${getLabelTranslation('reusableForm_EditRegistrationOf_')} Administrador`}
    >
      <GDMConditionalRender canRender={isLoading}>
        <GDMContainer sx={{ margin: 'auto auto' }}>
          <GDMLoading type="circular" />
        </GDMContainer>
      </GDMConditionalRender>

      <GDMConditionalRender canRender={!isLoading}>
        <GDMDynamicFormFields
          {...{
            entityType,
            viewType: 'edit',
            formData: admin,
            isSubmiting,
            errors,
            formDataEvents: {
              onCancel: handleCancel,
              onChange: handleChange,
              onSubmit: handleSubmit,
            },
            formDataOptions: {
              roles: [],
              brands: [],
              cities: [],
              states: [],
              regions: [],
              gdmSellers: [],
              multipliers: [],
            },
          }}
        />
      </GDMConditionalRender>
    </GDMPageCard>
  );
};

export default AdminEditPage;
